// Replaces react-datatrans-light-box's script loading, which crashes on
// retry: it calls window.Datatrans.startPayment whenever the script TAG is in
// the DOM — but the tag also stays there after a failed/pending load (no
// onerror handler at all), so every next payment attempt throws
// "undefined is not an object (evaluating 'window.Datatrans.startPayment')".

declare global {
  interface Window {
    Datatrans?: {
      startPayment: (config: { [key: string]: any }) => void
      close: () => void
    }
  }
}

const getScriptUrl = (production: boolean) =>
  production
    ? 'https://pay.datatrans.com/upp/payment/js/datatrans-2.0.0.min.js'
    : 'https://pay.sandbox.datatrans.com/upp/payment/js/datatrans-2.0.0.min.js'

const LOAD_TIMEOUT_MS = 15000

let loaderPromise: Promise<void> | null = null

export const loadDatatransScript = (production: boolean): Promise<void> => {
  if (window.Datatrans) {
    return Promise.resolve()
  }
  if (loaderPromise) {
    return loaderPromise
  }

  loaderPromise = new Promise<void>((resolve, reject) => {
    const src = getScriptUrl(production)

    // A leftover tag without window.Datatrans is a failed or aborted load —
    // replace it, a fresh tag re-triggers the fetch (browser dedupes via cache).
    document
      .querySelectorAll(`script[src="${src}"]`)
      .forEach((tag) => tag.remove())

    const script = document.createElement('script')
    script.src = src

    const fail = (reason: string) => {
      script.remove()
      loaderPromise = null
      reject(new Error(`Datatrans script: ${reason}`))
    }

    const timeout = window.setTimeout(
      () => fail(`load timed out after ${LOAD_TIMEOUT_MS}ms`),
      LOAD_TIMEOUT_MS
    )

    script.onload = () => {
      window.clearTimeout(timeout)
      if (window.Datatrans) {
        resolve()
      } else {
        fail('loaded but window.Datatrans is missing')
      }
    }
    script.onerror = () => {
      window.clearTimeout(timeout)
      fail('failed to load (network/blocker)')
    }

    document.body.appendChild(script)
  })

  return loaderPromise
}
